import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  BellOff, 
  AlertCircle, 
  CheckCircle,
  Settings,
  Monitor,
  Smartphone,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { toast } from 'react-hot-toast';
import { notificationService } from '@/services/notificationService';
import { desktopNotificationService } from '@/services/desktopNotificationService';

interface DesktopNotificationSettingsProps {
  onClose?: () => void;
}

const DesktopNotificationSettings: React.FC<DesktopNotificationSettingsProps> = ({ onClose }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isTestingNotification, setIsTestingNotification] = useState(false);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = () => {
    const supported = desktopNotificationService.isDesktopNotificationSupported();
    const permission = desktopNotificationService.getPermissionStatus();
    const enabled = notificationService.areDesktopNotificationsEnabled();

    setIsSupported(supported);
    setPermissionStatus(permission);
    setIsEnabled(enabled);
  };

  const handleToggleDesktopNotifications = async () => {
    if (!isSupported) {
      toast.error('Desktop notifications are not supported in this browser');
      return;
    }

    try {
      if (isEnabled) {
        // Disable desktop notifications
        notificationService.disableDesktopNotifications();
        setIsEnabled(false);
        toast.success('Desktop notifications disabled');
      } else {
        // Enable desktop notifications
        const enabled = await notificationService.enableDesktopNotifications();
        setIsEnabled(enabled);
        
        if (enabled) {
          toast.success('Desktop notifications enabled!');
          setPermissionStatus('granted');
        } else {
          toast.error('Failed to enable desktop notifications. Please check your browser permissions.');
        }
      }
    } catch (error) {
      console.error('Error toggling desktop notifications:', error);
      toast.error('Failed to update desktop notification settings');
    }
  };

  const handleTestNotification = async () => {
    if (!isEnabled) {
      toast.error('Please enable desktop notifications first');
      return;
    }

    setIsTestingNotification(true);
    
    try {
      await desktopNotificationService.showNotification(
        'Test Desktop Notification',
        {
          body: 'This is a test notification from Store Center. Desktop notifications are working!',
          icon: '/icon-192.png',
          tag: 'test-notification',
          requireInteraction: false
        }
      );
      
      toast.success('Test notification sent!');
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    } finally {
      setIsTestingNotification(false);
    }
  };

  const getPermissionBadge = () => {
    switch (permissionStatus) {
      case 'granted':
        return <Badge variant="success" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Granted
        </Badge>;
      case 'denied':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <X className="h-3 w-3" />
          Denied
        </Badge>;
      default:
        return <Badge variant="secondary" className="flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Default
        </Badge>;
    }
  };

  const getStatusMessage = () => {
    if (!isSupported) {
      return {
        type: 'error',
        message: 'Desktop notifications are not supported in this browser. Please use Chrome, Firefox, or Safari.'
      };
    }

    if (permissionStatus === 'denied') {
      return {
        type: 'error',
        message: 'Desktop notifications are blocked. Please reset permissions in your browser settings.'
      };
    }

    if (isEnabled) {
      return {
        type: 'success',
        message: 'Desktop notifications are active! You\'ll receive notifications even when the app is not in focus.'
      };
    }

    return {
      type: 'info',
      message: 'Enable desktop notifications to receive alerts even when the app is not in focus.'
    };
  };

  const statusMessage = getStatusMessage();

  return (
    <Card className="p-6 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Monitor className="h-5 w-5 text-blue-400" />
          <h3 className="text-lg font-semibold">Desktop Notifications</h3>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Status Message */}
        <div className={`p-3 rounded-lg border ${
          statusMessage.type === 'success' 
            ? 'bg-green-500/10 border-green-500/20 text-green-300' 
            : statusMessage.type === 'error'
            ? 'bg-red-500/10 border-red-500/20 text-red-300'
            : 'bg-blue-500/10 border-blue-500/20 text-blue-300'
        }`}>
          <div className="flex items-start gap-2">
            {statusMessage.type === 'success' && <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            {statusMessage.type === 'error' && <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            {statusMessage.type === 'info' && <Bell className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            <p className="text-sm">{statusMessage.message}</p>
          </div>
        </div>

        {/* Permission Status */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Permission Status:</span>
          {getPermissionBadge()}
        </div>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isEnabled ? (
              <Bell className="h-4 w-4 text-green-400" />
            ) : (
              <BellOff className="h-4 w-4 text-gray-400" />
            )}
            <span className="text-sm font-medium">Desktop Notifications</span>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={handleToggleDesktopNotifications}
            disabled={!isSupported}
          />
        </div>

        {/* Test Notification */}
        {isEnabled && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestNotification}
              disabled={isTestingNotification}
              className="w-full"
            >
              {isTestingNotification ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2" />
                  Sending Test...
                </>
              ) : (
                <>
                  <Bell className="h-3 w-3 mr-2" />
                  Send Test Notification
                </>
              )}
            </Button>
          </motion.div>
        )}

        {/* Info */}
        <div className="text-xs text-gray-400 bg-gray-800/50 p-3 rounded-lg">
          <p>💡 Desktop notifications work best when:</p>
          <ul className="mt-1 space-y-1">
            <li>• The app is installed as a PWA</li>
            <li>• Your browser allows notifications</li>
            <li>• You're using Chrome, Firefox, or Safari</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};

export default DesktopNotificationSettings; 